import * as React from 'react';
import { HonestWork } from '@cian/honestwork-component';
import { OwnerBadge } from '@cian/owner-badge-component';
import { plural } from '@cian/utils/lib/shared/plural';
import { mergeStyles } from '@cian/utils/lib/shared/style';
import { Stars } from '@cian/stars-component';
import {
  IAgentReviewsSchema,
  IAgentSchema,
  INewObjectBuilderSchema,
  IOfferSchema,
} from '@cian/api-models/offer-card/v1/get-offer-data';
import { Avatar } from '../../common/components/avatar';
import { Link } from '../../common/components/link';
import { LinkWrapper } from '../../common/components/link_wrapper';
import { Tag } from '../../common/components/tag';
import { AccountType } from '../../store/offer';
import {
  getAgentExperienceCIAN,
  getAgentLabel,
  getAgentName,
  getAgentType,
  getAuthorLabel,
  getProfileLink,
  getVerifyWay,
  isFromBuilder,
  isHomeowner,
  isHomeownerVerified,
  isOfficialSeller,
} from '../../utils/agent';
import { getNewbuildingsListQueryStringUrl } from '../../utils/formatters';
import { getOfferType, isCommercial } from '../../utils/offer_type';
import { getAgentExperience } from '../../utils/getAgentExperience';
import { OfferContactsAsideContainer } from '../contacts_aside/container';
import { getCardType, trackGoToCard } from './tracking';
import { PartnerTooltip } from '../PartnerTooltip';

import * as styles from './index.css';

export interface IAuthorAsideProps {
  agent: IAgentSchema | null;
  agentReviews?: IAgentReviewsSchema | null;
  developer: INewObjectBuilderSchema | null;
  offer: IOfferSchema;
  isAgentExperienceExperimentActive: boolean;
  showCountrysideBuilderBadge: boolean;
  isProAgent: boolean;
  isHonestAgent: boolean;
}

export class AuthorAside extends React.PureComponent<IAuthorAsideProps, {}> {
  public render() {
    const {
      agent,
      agentReviews,
      developer,
      offer,
      isAgentExperienceExperimentActive,
      showCountrysideBuilderBadge,
      isProAgent,
      isHonestAgent,
    } = this.props;

    const { status } = offer;
    const offerType = getOfferType(offer);

    if (!agent) {
      return null;
    }

    const { isCianPartner, isDeveloper, isSubAgent, accountType, status: agentStatus } = agent;

    /**
     * Указывает на сабагента с опубликованным мастер-аккаунтом
     *
     * В таком случае бекенд присылает урл аватара,
     * поэтому вычисляем по косвенному признаку - наличию аватара у субагента
     */
    const isSubagentWithPublishedMaster = isSubAgent && agent.absoluteAvatarUrl;
    const isSpecialist = accountType === 'specialist';
    const isPublishedAgent = agentStatus === 'published';
    const isAgentAndPublishedProfile = !isSubAgent && isPublishedAgent;

    const profileLink = getProfileLink(agent, offer, developer);

    const hasReviews = agentReviews && agentReviews.totalCount > 0;
    const hasRate = agentReviews && agentReviews.entityRate && agentReviews.entityRate > 0;
    const rate = (agentReviews && agentReviews.entityRate) || 0;
    const reviewsCount = (agentReviews && agentReviews.totalCount) || 0;

    const name = getAgentName(agent, offer);
    const label = getAuthorLabel(offer, showCountrysideBuilderBadge) || getAgentLabel(agent.accountType, offer);
    const isVerified = isHomeownerVerified(offer, agent);
    const verifyWay = getVerifyWay(offer, agent);

    const isFromBuilderOffer = isFromBuilder(offer);
    const isCommercialOffer = isCommercial(offerType);
    const isPublishedOffer = status === 'published';

    /**
     * Определение рендерит ли блок только 1 элемент внутри себя.
     */
    const isContainOneChild =
      !(isHonestAgent || isProAgent || label) &&
      !(isFromBuilderOffer && developer && developer.stats) &&
      !(isAgentAndPublishedProfile || (isSubAgent && agent.accountType) || isSubagentWithPublishedMaster) &&
      !(!isDeveloper && Boolean(agent.offersCount) && agent.offersLink) &&
      !(isFromBuilderOffer && profileLink) &&
      !(isPublishedOffer && isCommercialOffer);

    return (
      <div {...mergeStyles([styles['container'], isContainOneChild && styles['container--contain_one_child']])}>
        {(isHonestAgent || isProAgent || isCianPartner || label) && (
          <div className={styles['honest-container']}>
            {isHonestAgent && (
              <HonestWork
                agentLink={{
                  agentId: agent.id,
                  otherLinkProps: {
                    deal_type: offer.dealType,
                    offer_type: offer.offerType,
                  },
                }}
                className={styles['tag']}
              />
            )}

            {isProAgent && (
              <Tag {...mergeStyles([styles['tag'], styles['tag-pro']])} title="Профессиональный пользователь">
                Pro
              </Tag>
            )}

            {isCianPartner && (
              <PartnerTooltip master={<Tag {...mergeStyles([styles['tag'], styles['tag-partner']])}>Партнёр</Tag>} />
            )}

            {label &&
              (label === 'homeowner' ? (
                <OwnerBadge isVerified={isVerified} verifyWay={verifyWay} />
              ) : (
                <Tag className={styles['tag']}>{label}</Tag>
              ))}
          </div>
        )}
        <div className={styles['link-wrapper']}>
          <LinkWrapper
            className={styles['title-link']}
            href={profileLink}
            target="_blank"
            onClick={() => trackGoToCard(getCardType(offer), 'name')}
          >
            <h2 className={styles['title']}>{name || `ID ${agent.id}`}</h2>
          </LinkWrapper>
          {(isPublishedAgent || isSubagentWithPublishedMaster) && (
            <LinkWrapper href={profileLink} target="_blank" onClick={() => trackGoToCard(getCardType(offer), 'ava')}>
              <Avatar
                isSmall
                className={styles['image']}
                isRoundness={
                  agent.accountType !== AccountType.Agency &&
                  !isDeveloper &&
                  !isFromBuilderOffer &&
                  !isOfficialSeller(offer)
                }
                url={agent.absoluteAvatarUrl || ''}
                character={(!isHomeowner(offer) && name && name[0]) || ''}
              />
            </LinkWrapper>
          )}
        </div>
        {isFromBuilderOffer && developer && developer.stats && (
          <ul className={styles['company']}>
            {developer.stats.done && developer.stats.done.qs && developer.stats.done.text ? (
              <li className={styles['company-item']}>
                <span>Сдано</span>
                <Link
                  href={getNewbuildingsListQueryStringUrl(developer.stats.done.qs)}
                  target="_blank"
                  onClick={() => trackGoToCard(getCardType(offer), 'ready_JK')}
                >
                  {developer.stats.done.text}
                </Link>
              </li>
            ) : (
              <li className={styles['company-item']}>
                <span>Нет сданных ЖК</span>
              </li>
            )}
            {developer.stats.progress && developer.stats.progress.qs && developer.stats.progress.text ? (
              <li className={styles['company-item']}>
                <span>Строится</span>
                <Link
                  href={getNewbuildingsListQueryStringUrl(developer.stats.progress.qs)}
                  target="_blank"
                  onClick={() => trackGoToCard(getCardType(offer), 'in_progress_JK')}
                >
                  {developer.stats.progress.text}
                </Link>
              </li>
            ) : (
              <li className={styles['company-item']}>
                <span>Нет строящихся ЖК</span>
              </li>
            )}
          </ul>
        )}
        {(isAgentAndPublishedProfile || (isSubAgent && agent.accountType)) && (
          <div className={styles['type']}>{getAgentType(agent, offer)}</div>
        )}
        {agent.experience && isSpecialist && isAgentExperienceExperimentActive && (
          <div className={styles['experience']}>
            {getAgentExperience(agent)}
            <br />
            {getAgentExperienceCIAN(agent)}
          </div>
        )}
        {isSpecialist && isPublishedAgent && (
          <div className={styles['reviews']}>
            {hasRate && (
              <div className={styles['rate-wrapper']}>
                <div className={styles['rate']}>
                  {(rate * 5)
                    .toFixed(1)
                    .toString()
                    .replace('.', ',')}
                </div>
                <div className={styles['stars']}>
                  <Stars value={rate} percentage uniqueKey={`${agent.id}`} unitClassName={styles['reviews-star']} />
                </div>
              </div>
            )}
            {!hasReviews && <div className={styles['no-reviews']}>Нет отзывов</div>}
            {hasReviews && hasRate && (
              <div className={styles['reviews-count']}>
                <Link className={styles['reviews-count-link']} href={`/agents/${agent.id}`}>
                  {reviewsCount} {plural(reviewsCount, ['отзыв', 'отзыва', 'отзывов'])}
                </Link>
              </div>
            )}
          </div>
        )}
        {!isDeveloper && Boolean(agent.offersCount) && agent.offersLink && (
          <Link
            className={styles['offers-link']}
            href={agent.offersLink}
            target="_blank"
            onClick={() => trackGoToCard(getCardType(offer), 'qty')}
          >
            {agent.offersCount} {plural(Number(agent.offersCount), ['объявление', 'объявления', 'объявлений'])}
          </Link>
        )}
        {isFromBuilderOffer && profileLink && (
          <Link
            className={styles['offers-link']}
            href={profileLink}
            target="_blank"
            onClick={() => trackGoToCard(getCardType(offer), 'more')}
          >
            Подробнее о застройщике
          </Link>
        )}
        {isPublishedOffer && isCommercialOffer && <OfferContactsAsideContainer />}
      </div>
    );
  }
}
